// src/utils/productLabels.ts
import { ProductStatus } from '@models/product';
import { ProductSaleType, ProductType } from '@apis/product';
import type { UiMeta } from '@constants/uiMeta';
import { getUiLabel, getUiMeta, toLabelMap } from '@constants/uiMeta';

/**
 * User-facing metadata for product status.
 */
export const PRODUCT_STATUS_META = {
  [ProductStatus.ACTIVE]: {
    label: 'Active',
    chip: { color: 'success', variant: 'filled' },
    order: 1,
  },
  [ProductStatus.OUT_OF_STOCK]: {
    label: 'Out of Stock',
    chip: { color: 'warning', variant: 'filled' },
    order: 2,
  },
  [ProductStatus.DISCONTINUED]: {
    label: 'Discontinued',
    chip: { color: 'default', variant: 'outlined' },
    order: 3,
  },
} as const satisfies Record<ProductStatus, UiMeta>;

/**
 * User-facing metadata for sale type (order type).
 */
export const PRODUCT_SALE_TYPE_META = {
  [ProductSaleType.REAL_TIME]: {
    label: 'Real-time',
    chip: { color: 'default', variant: 'filled' },
    order: 1,
  },
  [ProductSaleType.RESERVATION]: {
    label: 'Reservation',
    chip: { color: 'default', variant: 'outlined' },
    order: 2,
  },
} as const satisfies Record<ProductSaleType, UiMeta>;

/**
 * User-facing metadata for product type.
 */
export const PRODUCT_TYPE_META = {
  [ProductType.ESIM]: {
    label: 'eSIM',
    chip: { color: 'primary', variant: 'filled' },
    order: 1,
  },
  [ProductType.PSIM_TOPUP]: {
    label: 'USIM Top-up',
    chip: { color: 'info', variant: 'outlined' },
    order: 2,
  },
  [ProductType.RESERVE_ESIM]: {
    label: 'SIM Pickup',
    chip: { color: 'secondary', variant: 'outlined' },
    order: 3,
  },
  [ProductType.ROUTER]: {
    label: 'Router Rental',
    chip: { color: 'default', variant: 'outlined' },
    order: 4,
  },
} as const satisfies Record<ProductType, UiMeta>;

export const PRODUCT_STATUS_LABELS = toLabelMap(PRODUCT_STATUS_META);
export const PRODUCT_SALE_TYPE_LABELS = toLabelMap(PRODUCT_SALE_TYPE_META);
export const PRODUCT_TYPE_LABELS = toLabelMap(PRODUCT_TYPE_META);

export const getProductStatusMeta = (
  status?: ProductStatus | string | null,
) => getUiMeta(PRODUCT_STATUS_META, status);

export const getProductStatusLabel = (
  status?: ProductStatus | string | null,
) => getUiLabel(PRODUCT_STATUS_META, status);

export const getProductSaleTypeMeta = (
  saleType?: ProductSaleType | string | null,
) => getUiMeta(PRODUCT_SALE_TYPE_META, saleType);

export const getProductSaleTypeLabel = (
  saleType?: ProductSaleType | string | null,
) => getUiLabel(PRODUCT_SALE_TYPE_META, saleType);

export const getProductTypeMeta = (
  productType?: ProductType | string | null,
) => getUiMeta(PRODUCT_TYPE_META, productType);

export const getProductTypeLabel = (
  productType?: ProductType | string | null,
) => getUiLabel(PRODUCT_TYPE_META, productType);

/**
 * Generic helper to safely get label.
 */
export const getLabel = <T extends string>(
  map: Record<T, string>,
  value: T | null | undefined,
): string => {
  if (!value) return '-';
  return map[value] ?? value;
};
